import { HonorsClubControllerService } from '@/__generated__/FrontApi';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import LabelText from '@/components/Form/LabelText';
import InputCheckbox from '@/components/Input/InputCheckbox';
import InputNumber from '@/components/Input/InputNumber';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import { emailRegex, mobileRegex } from '@/helpers/ValidationHelper';
import { usePopupStore } from '@/stores/PopupStore';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

export interface HonorsClubData {
  userName: string;
  userMobile: string;
  userEmail: string;
  agreeCheck: boolean;
  formType: string;
  inquiryType?: string;
  inquiry?: string;
}
interface HonorsCardProps {
  formType: string; // 'honors' | 'intl'
  onComplete: (isComplete: boolean) => void; // 완료후 true 반환
}

const HonorsInquiryCard: FC<HonorsCardProps> = ({ formType, onComplete }) => {
  const {
    register,
    handleSubmit,
    getValues,
    control,
    reset,
    formState: { errors },
  } = useForm<HonorsClubData>({
    defaultValues: {
      userName: ``,
      userMobile: ``,
      userEmail: ``,
      agreeCheck: false,
      inquiryType: ``,
      inquiry: ``,
    },
  });
  const popupStore = usePopupStore();
  const onSubmit: SubmitHandler<HonorsClubData> = useCallback(
    async ({
      userName,
      userMobile,
      userEmail,
      agreeCheck,
      inquiryType,
      inquiry,
      ...formData
    }) => {
      try {
        const {
          resultCode,
          resultMessage,
        } = await HonorsClubControllerService.insertHonorsQnaUsingPost({
          ...formData,
          name: userName,
          mobile: userMobile,
          email: userEmail,
          isAgreeYn: agreeCheck ? `Y` : `N`,
          type: formType,
          inquiryType: inquiryType,
          inquiry: inquiry,
        });
        if (resultCode === 'success') {
          reset();
          onComplete(true);
        } else {
          onComplete(false);
        }
        popupStore.show(resultMessage);
      } catch {
        popupStore.show(`문의 등록에 실패했습니다. 다시 시도해주세요.`);
      }
    },
    [popupStore, reset, formType, onComplete],
  );

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-container">
          <FormGroup>
            <LabelText require>성명</LabelText>
            <InputText
              placeholder="성명 입력"
              ref={register({
                required: {
                  value: true,
                  message: `성명을 입력해주세요.`,
                },
                maxLength: {
                  value: 20,
                  message: `20자 이내로 입력해주세요.`,
                },
              })}
              name="userName"
            />
            {errors.userName && (
              <ErrorMessage>{errors.userName.message}</ErrorMessage>
            )}
          </FormGroup>
          <div className="clear" />
          <FormGroup>
            <LabelText require>연락처</LabelText>
            <Controller
              control={control}
              name="userMobile"
              rules={{
                required: {
                  value: true,
                  message: `연락처를 입력해주세요.`,
                },
                pattern: {
                  value: mobileRegex,
                  message: `올바른 연락처 형식이 아닙니다.`,
                },
              }}
              as={
                <InputNumber
                  format="###########"
                  placeholder="연락처 입력 ( &lsquo;-&rsquo; 제외 )"
                />
              }
            />
            {errors.userMobile && (
              <ErrorMessage>{errors.userMobile.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup>
            <LabelText require>이메일</LabelText>
            <InputText
              name="userEmail"
              ref={register({
                required: {
                  value: true,
                  message: `이메일을 입력해주세요.`,
                },
                pattern: {
                  value: emailRegex,
                  message: `올바른 이메일 형식이 아닙니다.`,
                },
                maxLength: {
                  value: 100,
                  message: `100자 이내로 입력해주세요.`,
                },
              })}
              placeholder="이메일주소 입력"
            />
            {errors.userEmail && (
              <ErrorMessage>{errors.userEmail.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup className="full">
            <LabelText>문의사항</LabelText>
            <InputTextarea
              name="inquiry"
              placeholder="문의사항을 입력해주세요."
              ref={register({
                required: {
                  value: true,
                  message: `문의사항을 입력해주세요.`,
                },
              })}
            />
          </FormGroup>
          <FormGroup className="full">
            <InputCheckbox
              className="chk-label"
              ref={register({
                required: {
                  value: true,
                  message: `약관 동의에 체크해주세요.`,
                },
              })}
              name="agreeCheck"
              label="예, 저는 맞춤안내를 받고싶습니다. 그리고 안내를 원치 않을 시, 언제든지 수신거부 할 수 있음을 잘 알고 있습니다."
            />
            {errors.agreeCheck && (
              <ErrorMessage>{errors.agreeCheck.message}</ErrorMessage>
            )}
          </FormGroup>
        </div>
        <div className="btn-wrap">
          <Button className="btn" type="submit">
            문의하기
          </Button>
        </div>
      </form>
    </>
  );
};
export default HonorsInquiryCard;
